import { useEffect, useRef, useState } from "react";
import { Upload, FileSearch, Database } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageHeader, Card } from "@/components/Ui";
import { Button } from "@/components/ui/button";

function Report({ r }) {
  return (
    <Card className="p-5" data-testid="import-report">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold">{r.dry_run ? "Anteprima (nessun dato scritto)" : "Import completato"}</h3>
        {r.safety_backup_id && <span className="text-xs text-slate-500">Backup di sicurezza: <span className="font-mono">{r.safety_backup_id.slice(0, 8)}</span></span>}
      </div>
      <div className="overflow-x-auto"><table className="w-full text-sm text-left min-w-[420px]">
        <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
          <tr><th className="py-2">Tabella Manus</th><th>Trovati</th><th>Nuovi</th><th>Aggiornati</th><th>Saltati</th></tr>
        </thead>
        <tbody>
          {Object.entries(r.tabelle).map(([t, s]) => (
            <tr key={t} className="border-b border-slate-100" data-testid={`import-row-${t}`}>
              <td className="py-2 font-mono text-xs">{t}</td><td>{s.trovati}</td><td className="text-emerald-700">{s.importati}</td><td>{s.aggiornati}</td><td className="text-slate-400">{s.saltati}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      {r.tabelle_ignorate.length > 0 && <p className="text-xs text-slate-500 mt-3">Tabelle non importate (integrazioni non ancora attive): {r.tabelle_ignorate.join(", ")}</p>}
      {r.avvisi_totali > 0 && (
        <div className="mt-4">
          <p className="text-xs font-semibold text-amber-700 mb-1">{r.avvisi_totali} avvisi (riferimenti non trovati)</p>
          <ul className="text-xs text-slate-600 max-h-40 overflow-y-auto space-y-0.5">{r.avvisi.map((a, i) => <li key={i}>{a}</li>)}</ul>
        </div>
      )}
    </Card>
  );
}

export default function ImportData() {
  const fileRef = useRef();
  const [skipTest, setSkipTest] = useState(true);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/import/history").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const run = async (dry) => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Seleziona lo zip esportato da Manus");
    if (!dry && !window.confirm("Confermi l'import? Verrà creato prima un backup di sicurezza.")) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("dry_run", dry);
    fd.append("skip_test", skipTest);
    setBusy(true);
    try {
      const { data } = await api.post("/import/manus", fd);
      setReport(data);
      toast.success(dry ? "Anteprima pronta" : "Dati importati");
      if (!dry) loadHistory();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Migrazione dal CRM Manus" title="Import dati" subtitle="Carica lo zip con le tabelle esportate da Manus (JSON o CSV, un file per tabella). Prima esegui l'anteprima, poi l'import: è ripetibile senza creare duplicati." />
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        <Card className="p-6 space-y-4 h-fit">
          <input type="file" accept=".zip,.json,.csv" ref={fileRef} className="block w-full text-sm" data-testid="import-file-input" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="accent-[#0A2540]" checked={skipTest} onChange={(e) => setSkipTest(e.target.checked)} data-testid="import-skip-test" />
            Escludi i dati di prova (is_test)
          </label>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={busy} onClick={() => run(true)} data-testid="import-preview-button"><FileSearch className="h-4 w-4 mr-1" />Anteprima</Button>
            <Button disabled={busy || !report} onClick={() => run(false)} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="import-run-button"><Upload className="h-4 w-4 mr-1" />{busy ? "Elaborazione…" : "Importa"}</Button>
          </div>
          <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
            <li>I collegamenti tra Trust, soggetti, patrimonio, contabilità e documenti vengono ricostruiti in automatico.</li>
            <li>Gli utenti diventano utenze OTP con il loro profilo; quelli già presenti (es. bruno@trustmaster.it) non vengono toccati. I PIN non vengono importati.</li>
            <li>Dei documenti si importano solo i dati: i file si caricheranno in un secondo momento.</li>
            <li>L'import viene registrato nel log di audit.</li>
          </ul>
        </Card>
        <div className="space-y-6">
          {report && <Report r={report} />}
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Database className="h-4 w-4" />Storico import</h3>
            {history.length === 0 && <p className="text-sm text-slate-400">Nessun import eseguito</p>}
            {history.map((h) => (
              <div key={h.id} className="text-xs border-l-2 border-sky-400 pl-3 mb-2">
                <p className="font-medium">{h.file} · {fmtDateTime(h.created_at)}</p>
                <p className="text-slate-500">{h.by} · {Object.entries(h.report.tabelle).map(([t, s]) => `${t}: ${s.importati + s.aggiornati}`).join(" · ")}</p>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}
